import React, { useEffect, useRef, useState } from "react";

/* The public site key. reCAPTCHA site keys are meant to be in the page; the
   secret one lives in send-email.php and never leaves the server.

   It used to be the literal string YOUR_SITE_KEY_HERE, substituted by
   deploy-working.sh at deploy time. The GitHub Action does not run that script,
   so every build shipped by a push to master went out with the placeholder,
   the widget never rendered, and send-email.php rejected the submission for a
   missing token. Anything that has to be true in production belongs in the
   repo, not in one of the two ways of deploying it. */
export const SITE_KEY = "6LdFlm8rAAAAALB2CYOKfvAHR_lPcsDDOByRIu74";

const SRC = "https://www.google.com/recaptcha/api.js?render=explicit";

/* The form loads its own dependency rather than relying on a tag in
   public/index.html. That file is read once when the dev server starts, so an
   edit to it does nothing until somebody restarts, which looks exactly like a
   broken widget and is the reason this was rewritten. Owning the script here
   means the component works the same in dev, in a production build, and on a
   page that was cached before the tag existed. */
let loading = null;
function loadApi() {
  if (window.grecaptcha && typeof window.grecaptcha.render === "function") {
    return Promise.resolve();
  }
  if (loading) return loading;

  loading = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src^="${SRC.split("?")[0]}"]`);
    if (!existing) {
      const s = document.createElement("script");
      s.src = SRC;
      s.async = true;
      s.defer = true;
      s.onerror = () => reject(new Error("blocked"));
      document.head.appendChild(s);
    }
    /* api.js defines window.grecaptcha immediately as a stub carrying only
       ready(), then fetches the real library from gstatic. render() does not
       exist until that second file lands, so waiting on the script's onload is
       not enough; this waits for the method it is actually going to call. */
    const t0 = Date.now();
    const poll = () => {
      if (window.grecaptcha && typeof window.grecaptcha.render === "function") {
        resolve();
        return;
      }
      if (Date.now() - t0 > 15000) {
        reject(new Error("timeout"));
        return;
      }
      setTimeout(poll, 120);
    };
    poll();
  });
  return loading;
}

export default function Recaptcha({ theme, onReady }) {
  const host = useRef(null);
  const widget = useRef(null);
  const [problem, setProblem] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let dead = false;

    loadApi()
      .then(() => {
        if (dead || !host.current) return;
        /* render() throws if this element already holds a widget, which is
           what happens on the second pass of a StrictMode double mount. A
           fresh child each time gives grecaptcha an element it has not seen. */
        host.current.innerHTML = "";
        const slot = document.createElement("div");
        host.current.appendChild(slot);
        widget.current = window.grecaptcha.render(slot, {
          sitekey: SITE_KEY,
          theme: theme === "light" ? "light" : "dark",
        });
        setReady(true);
        setProblem(null);
        onReady({
          getResponse: () => window.grecaptcha.getResponse(widget.current),
          reset: () => window.grecaptcha.reset(widget.current),
        });
      })
      .catch((err) => {
        if (dead) return;
        setProblem(err && err.message === "blocked" ? "blocked" : "slow");
        // eslint-disable-next-line no-console
        console.error("[recaptcha]", err);
      });

    return () => {
      dead = true;
    };
    // theme is a dependency on purpose: a widget cannot be re-themed in place,
    // so it is torn down and rendered again when the site flips.
  }, [theme, onReady]);

  return (
    <div className="captcha">
      <div ref={host} />

      {!ready && !problem && <span className="captcha__note">Loading the human check</span>}

      {problem && (
        <span className="captcha__note bad">
          {problem === "blocked"
            ? "The human check was blocked before it could load, usually by an ad blocker or a strict network."
            : "The human check is taking longer than it should to load."}{" "}
          Mail us on info@sipnscore.co.za and we will pick it up there.
        </span>
      )}
    </div>
  );
}
